import React, { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Navbar, Nav } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import axios from "axios";
import { AuthContext } from "../AuthContext";
import { parseStoredAccessToken } from "../parseStoredAccessToken";
import { site_url } from "../config";

const rupee = (value) =>
  `Rs.${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const cardDigits = (value) => String(value || "").replace(/\D/g, "").slice(0, 16);

const formatCard = (value) => cardDigits(value).replace(/(\d{4})(?=\d)/g, "$1 ").trim();

export default function CreditCard() {
  const { user, loading } = useContext(AuthContext);
  const [card, setCard] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [rules, setRules] = useState({
    enabled: true,
    min_wallet_amount: 0,
    max_payment_amount: 50000,
    wallet_balance: null,
    paid_this_month: false,
    this_month: null,
    instant_bonus_percent: 0,
    history: [],
  });

  const authHeaders = () => {
    const token = parseStoredAccessToken(user);
    return {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };
  };

  const loadRules = async () => {
    const token = parseStoredAccessToken(user);
    if (!token) return;
    try {
      const res = await axios.get(`${site_url}/credit-card/limits`, { headers: authHeaders() });
      const settings = res.data?.settings || {};
      setRules({
        enabled: settings.enabled !== false,
        min_wallet_amount: Number(settings.min_wallet_amount) || 0,
        max_payment_amount: Number(settings.max_payment_amount) || 0,
        wallet_balance: res.data?.wallet_balance ?? null,
        paid_this_month: !!res.data?.paid_this_month,
        this_month: res.data?.this_month || null,
        instant_bonus_percent: Number(res.data?.instant_bonus_percent) || 0,
        history: res.data?.history || [],
      });
    } catch (err) {
      setError(err.response?.data?.message || "Credit card payment details could not be loaded.");
    }
  };

  useEffect(() => {
    loadRules();
  }, [user]);

  if (loading) {
    return <div style={{ minHeight: "100vh", background: "#f4f7fb" }} />;
  }
  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  const payAmount = Number(amount) || 0;
  const wallet = rules.wallet_balance == null ? null : Number(rules.wallet_balance);
  const maxAmount = Number(rules.max_payment_amount) || 0;
  const minWallet = Number(rules.min_wallet_amount) || 0;
  const overMax = payAmount > maxAmount && maxAmount > 0;
  const belowMinWallet = wallet != null && wallet < minWallet;
  const shortWallet = wallet != null && payAmount > 0 && wallet < payAmount;
  const bonus = payAmount > 0 && rules.instant_bonus_percent > 0
    ? Math.round(((payAmount * rules.instant_bonus_percent) / 100) * 100) / 100
    : 0;
  const canPay = rules.enabled
    && !rules.paid_this_month
    && cardDigits(card).length === 16
    && payAmount >= 1
    && !overMax
    && !belowMinWallet
    && !shortWallet
    && !submitting;

  const place = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({});
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      }),
      () => resolve({}),
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 60000 }
    );
  });

  const submit = async (event) => {
    event.preventDefault();
    if (!canPay) return;
    setError("");
    setResult(null);
    setSubmitting(true);
    try {
      const coords = await place();
      const res = await axios.post(
        `${site_url}/credit-card/pay`,
        {
          card_number: cardDigits(card),
          amount: payAmount,
          ...coords,
        },
        { headers: authHeaders() }
      );
      setResult(res.data);
      setCard("");
      setAmount("");
      loadRules();
    } catch (err) {
      setError(err.response?.data?.message || "Credit card payment could not be sent.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f4f7fb", paddingBottom: 40 }}>
      <Navbar expand="lg" className="px-3" style={navStyle}>
        <Navbar.Brand className="fw-bold text-white">
          <FaArrowLeft
            onClick={() => window.history.back()}
            className="fs-5 text-white"
            style={{ cursor: "pointer", marginRight: 8 }}
          />
          Credit Card Payment
        </Navbar.Brand>
        <Nav className="ms-auto" />
      </Navbar>

      <div className="container" style={{ maxWidth: 520, marginTop: 20 }}>
        <div className="card shadow-sm" style={{ borderRadius: 16, border: "none" }}>
          <div className="card-body p-4">
            {!rules.enabled ? (
              <div className="alert alert-warning mb-0">Credit card payment is turned off.</div>
            ) : (
              <form onSubmit={submit}>
                <p className="text-muted small">
                  One payment each month. Wallet amount only. Online payment and partial payment are not available.
                  Welcome bonus is not used.
                </p>
                {rules.paid_this_month && (
                  <div className="alert alert-info py-2">
                    Credit card payment is allowed once this month
                    {rules.this_month?.orderid ? ` · Bill ${rules.this_month.orderid}` : ""}.
                  </div>
                )}
                <label className="form-label fw-semibold">Credit card number</label>
                <input
                  className="form-control mb-3"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  value={formatCard(card)}
                  onChange={(e) => setCard(cardDigits(e.target.value))}
                  placeholder="XXXX XXXX XXXX XXXX"
                  required
                />
                <label className="form-label fw-semibold">
                  Amount (up to {rupee(maxAmount)})
                </label>
                <input
                  className="form-control mb-3"
                  type="number"
                  min="1"
                  max={maxAmount || undefined}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Amount"
                  required
                />
                {overMax && (
                  <div className="alert alert-info py-2">
                    Your max credit card payment amount is {rupee(maxAmount)}.
                  </div>
                )}
                {belowMinWallet && (
                  <div className="alert alert-info py-2">
                    Minimum wallet amount is {rupee(minWallet)}. Your wallet is {rupee(wallet)}.
                  </div>
                )}
                {shortWallet && !belowMinWallet && (
                  <div className="alert alert-info py-2">
                    Wallet balance is not enough. Online and partial payment are not available. Your wallet is {rupee(wallet)}.
                  </div>
                )}
                <p className="text-muted small">
                  Minimum wallet {rupee(minWallet)}
                  {wallet != null ? ` · your wallet ${rupee(wallet)}` : ""}
                  . Max payment {rupee(maxAmount)}.
                </p>
                {bonus > 0 && (
                  <p className="small mb-3" style={{ color: "#1f7a4d" }}>
                    Instant bonus {rupee(bonus)} will be credited to your wallet.
                  </p>
                )}
                <button
                  type="submit"
                  className="btn w-100 text-white"
                  style={{ background: "#5ca4d6", borderRadius: 12, fontWeight: 700 }}
                  disabled={!canPay}
                >
                  {submitting ? "Please wait..." : `Pay ${rupee(payAmount)} from wallet`}
                </button>
              </form>
            )}

            {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
            {result && (
              <div className={`alert mt-3 mb-0 ${result.status === "failed" ? "alert-danger" : "alert-success"}`}>
                <div className="fw-semibold text-capitalize">{result.status}</div>
                <div>{result.message}</div>
                {result.orderid && (
                  <div className="mt-2">
                    <div className="fw-semibold">Purchase bill</div>
                    <div>Bill : {result.orderid}</div>
                    <div>Bill amount: {rupee(result.amount)}</div>
                    {result.account_no && <div>Card: {result.account_no}</div>}
                  </div>
                )}
                {Number(result.instant_bonus) > 0 && (
                  <div>Instant bonus credited: {rupee(result.instant_bonus)}</div>
                )}
                {result.redemption_balance != null && (
                  <div>Wallet balance: {rupee(result.redemption_balance)}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {rules.history.length > 0 && (
          <div className="card shadow-sm mt-3" style={{ borderRadius: 16, border: "none" }}>
            <div className="card-body">
              <h6 className="fw-bold">Recent credit card payments</h6>
              {rules.history.map((row) => (
                <div key={row.orderid} className="border-bottom py-2">
                  <div className="d-flex justify-content-between">
                    <strong>{row.account_no}</strong>
                    <span>{rupee(row.amount)}</span>
                  </div>
                  <div className="small text-muted">
                    {row.orderid} · {row.status}
                    {row.created_at ? ` · ${new Date(row.created_at).toLocaleString("en-IN")}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const navStyle = {
  background: "#5ca4d6",
  borderBottomLeftRadius: "16px",
  borderBottomRightRadius: "16px",
  padding: "18px 12px",
};
